import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  Pencil,
  Eye,
  Save,
  Sparkles,
  Plus,
  Trash2,
  GripVertical,
} from 'lucide-react';
import SuperAdminShell from './SuperAdminShell';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import {
  ALL_PLAN_SLUGS,
  PLAN_SLUG_LABELS,
  centsToDisplay,
  displayToCents,
  fetchAdminAddons,
  fetchAdminPlans,
  fetchCatalogPreview,
  fetchEnterpriseConfigs,
  createEnterpriseConfig,
  patchEnterpriseConfig,
  deleteEnterpriseConfig,
  fetchSystemSettings,
  isPriceChangeConfirmationError,
  priceChangeDetail,
  patchAdminAddon,
  patchAdminPlan,
  patchSystemSettings,
  yearlySavingsPercent,
  yearlyMonthlyEquivalent,
} from '../../lib/adminCatalogApi';
import PriceChangeConfirmDialog from '../../components/superadmin/PriceChangeConfirmDialog';
import { API_URL } from '../../config';
import { planDisplayPrice } from '../../lib/billingApi';

function formatUsd(cents) {
  if (cents == null) return '—';
  return `$${(Number(cents) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function newFeatureId() {
  return `feature_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function normalizePlanFeatures(features) {
  return (features || [])
    .map((f) => ({
      id: f.id || newFeatureId(),
      label: (f.label || '').trim(),
      included: true,
      note: f.note?.trim() ? f.note.trim() : null,
    }))
    .filter((f) => f.label);
}

function PlanFeaturesEditor({ features, onChange }) {
  const [newLabel, setNewLabel] = useState('');

  const updateLabel = (id, label) => {
    onChange(
      (features || []).map((f) => (f.id === id ? { ...f, label, included: true } : f))
    );
  };

  const removeFeature = (id) => {
    onChange((features || []).filter((f) => f.id !== id));
  };

  const addFeature = () => {
    const label = newLabel.trim();
    if (!label) return;
    onChange([
      ...(features || []),
      { id: newFeatureId(), label, included: true, note: null },
    ]);
    setNewLabel('');
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        These bullets appear on the public pricing page, in order.
      </p>
      <div className="space-y-2">
        {(features || []).length === 0 ? (
          <p className="text-sm text-slate-500 border rounded-lg p-3 bg-slate-50 dark:bg-slate-900/50">
            No features yet. Add your first one below.
          </p>
        ) : (
          (features || []).map((f) => (
            <div key={f.id} className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-slate-300 shrink-0" aria-hidden />
              <Input
                className="h-9 flex-1"
                value={f.label || ''}
                placeholder="Feature text"
                onChange={(e) => updateLabel(f.id, e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => removeFeature(f.id)}
                aria-label="Remove feature"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
      </div>
      <div className="flex gap-2">
        <Input
          className="h-9 flex-1"
          placeholder="e.g. Unlimited readers"
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addFeature();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={addFeature}>
          <Plus className="w-4 h-4 mr-1" />
          Add
        </Button>
      </div>
    </div>
  );
}

function LimitInput({ label, value, onChange, allowUnlimited }) {
  const isUnlimited = value === 'unlimited' || value === -1;
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-2 items-center">
        {allowUnlimited ? (
          <Switch
            checked={isUnlimited}
            onCheckedChange={(u) => onChange(u ? 'unlimited' : 1)}
            aria-label={`${label} unlimited`}
          />
        ) : null}
        {!isUnlimited || !allowUnlimited ? (
          <Input
            type="number"
            min={0}
            className="h-8"
            value={isUnlimited ? '' : value ?? ''}
            disabled={allowUnlimited && isUnlimited}
            onChange={(e) => onChange(Number(e.target.value) || 0)}
          />
        ) : (
          <span className="text-xs text-slate-500">Unlimited</span>
        )}
      </div>
    </div>
  );
}

function PlanEditorSheet({ plan, open, onOpenChange, addons, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moPrice, setMoPrice] = useState('');
  const [yrPrice, setYrPrice] = useState('');
  const [addonAvail, setAddonAvail] = useState({});
  const [priceConfirm, setPriceConfirm] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [origMoCents, setOrigMoCents] = useState(null);
  const [origYrCents, setOrigYrCents] = useState(null);

  useEffect(() => {
    if (!plan) return;
    setDraft(JSON.parse(JSON.stringify(plan)));
    setOrigMoCents(plan.monthly_price_cents ?? null);
    setOrigYrCents(plan.yearly_price_cents ?? null);
    setMoPrice(centsToDisplay(plan.monthly_price_cents));
    setYrPrice(centsToDisplay(plan.yearly_price_cents));
    const avail = {};
    (addons || []).forEach((a) => {
      avail[a.id] = (a.available_on_plans || []).includes(plan.id);
    });
    setAddonAvail(avail);
    setDirty(false);
    setPriceConfirm(null);
  }, [plan, open, addons]);

  const patch = (updates) => {
    setDraft((d) => ({ ...d, ...updates }));
    setDirty(true);
  };

  const patchLimit = (key, val) => {
    setDraft((d) => ({ ...d, limits: { ...(d.limits || {}), [key]: val } }));
    setDirty(true);
  };

  const setFeatures = (next) => {
    setDraft((d) => {
      const resolved = typeof next === 'function' ? next(d.features || []) : next;
      return { ...d, features: resolved };
    });
    setDirty(true);
  };

  const toggleIncludedAddon = (addonId) => {
    setDraft((d) => {
      const cur = d.included_addons || [];
      const next = cur.includes(addonId) ? cur.filter((x) => x !== addonId) : [...cur, addonId];
      return { ...d, included_addons: next };
    });
    setDirty(true);
  };

  const buildPayload = (confirmPrice) => {
    const moCents = displayToCents(moPrice);
    const yrCents = displayToCents(yrPrice);
    const payload = {
      name: draft.name,
      tagline: draft.tagline,
      is_enterprise_contact: draft.id === 'enterprise' ? true : draft.is_enterprise_contact,
      is_active: draft.is_active,
      is_highlighted: draft.is_highlighted,
      limits: draft.limits,
      features: normalizePlanFeatures(draft.features),
      included_addons: draft.included_addons || [],
      addon_availability: addonAvail,
      confirm_price_change: confirmPrice,
    };
    if (draft.id !== 'enterprise') {
      if (confirmPrice || moCents !== origMoCents) payload.monthly_price_cents = moCents;
      if (confirmPrice || yrCents !== origYrCents) payload.yearly_price_cents = yrCents;
    }
    return payload;
  };

  const save = async (confirmPrice = false) => {
    if (!draft) return;
    setSaving(true);
    try {
      await patchAdminPlan(draft.id, buildPayload(confirmPrice));
      toast.success('Plan saved');
      setDirty(false);
      setConfirmOpen(false);
      setPriceConfirm(null);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      if (isPriceChangeConfirmationError(e)) {
        setPriceConfirm(priceChangeDetail(e));
        setConfirmOpen(true);
      } else {
        const d = e.response?.data?.detail;
        const msg =
          typeof d === 'string'
            ? d
            : d?.message || (d?.code ? `${d.code}` : null) || `Save failed (${e.response?.status || 'error'})`;
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit {PLAN_SLUG_LABELS[draft.id] || draft.name}</SheetTitle>
          <SheetDescription>
            Changes sync to the public pricing page after save.
            {dirty ? ' · Unsaved changes' : null}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={draft.name || ''} onChange={(e) => patch({ name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Tagline</Label>
            <Textarea
              rows={2}
              value={draft.tagline || ''}
              onChange={(e) => patch({ tagline: e.target.value })}
            />
          </div>
          {draft.id === 'enterprise' ? (
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <Label>Contact Sales mode</Label>
                <p className="text-xs text-slate-500">Hide price; show Contact Sales CTA</p>
              </div>
              <Switch
                checked={!!draft.is_enterprise_contact}
                onCheckedChange={(v) => patch({ is_enterprise_contact: v })}
              />
            </div>
          ) : null}
          {draft.id !== 'enterprise' && !draft.is_enterprise_contact ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Monthly price (USD)</Label>
                <Input value={moPrice} onChange={(e) => { setMoPrice(e.target.value); setDirty(true); }} />
              </div>
              <div className="space-y-1">
                <Label>Yearly price (USD / yr)</Label>
                <Input value={yrPrice} onChange={(e) => { setYrPrice(e.target.value); setDirty(true); }} />
                {yrPrice ? (
                  <p className="text-xs text-slate-500">
                    ≈ ${(yearlyMonthlyEquivalent(displayToCents(yrPrice)) / 100).toFixed(2)}/mo ·{' '}
                    {yearlySavingsPercent(displayToCents(moPrice), displayToCents(yrPrice))}% vs monthly×12
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
          {draft.id === 'enterprise' ? (
            <p className="text-sm text-slate-500 rounded-lg border p-3 bg-slate-50 dark:bg-slate-900">
              Enterprise is always Contact Sales on the public pricing page. Assign per-company configs in the
              Enterprise tab.
            </p>
          ) : null}
          <Separator />
          <p className="text-sm font-medium">Limits</p>
          <div className="grid grid-cols-2 gap-3">
            <LimitInput
              label="Spaces"
              value={draft.limits?.spaces}
              allowUnlimited
              onChange={(v) => patchLimit('spaces', v)}
            />
            <LimitInput
              label="Team members included"
              value={draft.limits?.team_members_included}
              onChange={(v) => patchLimit('team_members_included', v)}
            />
            <LimitInput
              label="Custom domains"
              value={draft.limits?.custom_domains}
              allowUnlimited
              onChange={(v) => patchLimit('custom_domains', v)}
            />
            <LimitInput
              label="AI tokens / month (millions)"
              value={draft.limits?.ai_tokens_monthly}
              allowUnlimited
              onChange={(v) => patchLimit('ai_tokens_monthly', v)}
            />
          </div>
          <Separator />
          <p className="text-sm font-medium">Pricing page features</p>
          <PlanFeaturesEditor features={draft.features || []} onChange={setFeatures} />
          <Separator />
          <p className="text-sm font-medium">Add-ons available for this plan</p>
          <div className="space-y-2">
            {(addons || []).map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm gap-2">
                <div className="min-w-0">
                  <span>{a.name}</span>
                  <span className="block text-xs text-slate-500">
                    {formatUsd(a.monthly_price_cents)}/mo
                    {a.unit ? ` · ${a.unit}` : ''}
                  </span>
                </div>
                <Switch
                  checked={!!addonAvail[a.id]}
                  onCheckedChange={(v) => {
                    setAddonAvail((prev) => ({ ...prev, [a.id]: v }));
                    setDirty(true);
                  }}
                />
              </div>
            ))}
          </div>
          {draft ? (
            <>
              <Separator />
              <p className="text-sm font-medium">Included add-ons</p>
              <p className="text-xs text-slate-500">
                Automatically enabled for subscribers on this plan (disabled on cancel or downgrade).
              </p>
              <div className="space-y-2">
                {(addons || []).map((a) => (
                  <div key={`inc-${a.id}`} className="flex items-center justify-between text-sm">
                    <span>{a.name}</span>
                    <Switch
                      checked={(draft.included_addons || []).includes(a.id)}
                      onCheckedChange={() => toggleIncludedAddon(a.id)}
                    />
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
        <PriceChangeConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          entityName={draft.name}
          detail={priceConfirm}
          confirming={saving}
          onConfirm={() => save(true)}
        />
        <SheetFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Discard
          </Button>
          <Button onClick={() => save(false)} disabled={saving || !dirty}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save plan
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function AddonEditorSheet({ addon, open, onOpenChange, onSaved }) {
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [moPrice, setMoPrice] = useState('');
  const [yrPrice, setYrPrice] = useState('');
  const [extraMoPrice, setExtraMoPrice] = useState('');
  const [extraYrPrice, setExtraYrPrice] = useState('');
  const [priceConfirm, setPriceConfirm] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!addon) return;
    setDraft(JSON.parse(JSON.stringify(addon)));
    setMoPrice(centsToDisplay(addon.monthly_price_cents));
    setYrPrice(centsToDisplay(addon.yearly_price_cents));
    setExtraMoPrice(centsToDisplay(addon.extra_unit_monthly_price_cents));
    setExtraYrPrice(centsToDisplay(addon.extra_unit_yearly_price_cents));
    setDirty(false);
  }, [addon, open]);

  const patch = (updates) => {
    setDraft((d) => ({ ...d, ...updates }));
    setDirty(true);
  };

  const togglePlanList = (key, slug) => {
    setDraft((d) => {
      const cur = d[key] || [];
      const next = cur.includes(slug) ? cur.filter((x) => x !== slug) : [...cur, slug];
      return { ...d, [key]: next };
    });
    setDirty(true);
  };

  const save = async (confirmPrice = false) => {
    if (!draft) return;
    setSaving(true);
    try {
      await patchAdminAddon(draft.id, {
        name: draft.name,
        description: draft.description,
        type: draft.is_quantity_based ? 'per_unit' : 'flat',
        monthly_price_cents: displayToCents(moPrice),
        yearly_price_cents: displayToCents(yrPrice),
        unit: draft.unit || draft.unit_label || null,
        unit_label: draft.unit_label || null,
        is_active: draft.is_active,
        available_on_plans: draft.available_on_plans || [],
        included_in_plans: draft.included_in_plans || [],
        is_quantity_based: draft.is_quantity_based,
        is_tiered_quantity: draft.is_tiered_quantity,
        max_quantity: draft.max_quantity,
        extra_unit_monthly_price_cents: displayToCents(extraMoPrice),
        extra_unit_yearly_price_cents: displayToCents(extraYrPrice),
        confirm_price_change: confirmPrice,
      });
      toast.success('Add-on saved');
      setDirty(false);
      setConfirmOpen(false);
      setPriceConfirm(null);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      if (isPriceChangeConfirmationError(e)) {
        setPriceConfirm(priceChangeDetail(e));
        setConfirmOpen(true);
      } else {
        toast.error(e.response?.data?.detail || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit {draft.name}</SheetTitle>
          <SheetDescription>{dirty ? 'Unsaved changes' : 'Add-on catalog settings'}</SheetDescription>
        </SheetHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={draft.name || ''} onChange={(e) => patch({ name: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea rows={3} value={draft.description || ''} onChange={(e) => patch({ description: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Monthly (USD)</Label>
              <Input value={moPrice} onChange={(e) => { setMoPrice(e.target.value); setDirty(true); }} />
            </div>
            <div>
              <Label>Yearly (USD / yr)</Label>
              <Input value={yrPrice} onChange={(e) => { setYrPrice(e.target.value); setDirty(true); }} />
              {yrPrice ? (
                <p className="text-xs text-slate-500 mt-1">
                  ≈ ${(yearlyMonthlyEquivalent(displayToCents(yrPrice)) / 100).toFixed(2)}/mo ·{' '}
                  {yearlySavingsPercent(displayToCents(moPrice), displayToCents(yrPrice))}% savings
                </p>
              ) : null}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Unit label</Label>
            <Input
              placeholder="e.g. per member/month"
              value={draft.unit || ''}
              onChange={(e) => patch({ unit: e.target.value || null })}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label>Quantity-based</Label>
            <Switch checked={!!draft.is_quantity_based} onCheckedChange={(v) => patch({ is_quantity_based: v })} />
          </div>
          <div className="flex items-center justify-between">
            <Label>Tiered pricing (base + extra unit)</Label>
            <Switch
              checked={!!draft.is_tiered_quantity}
              onCheckedChange={(v) => patch({ is_tiered_quantity: v, is_quantity_based: v ? true : draft.is_quantity_based })}
            />
          </div>
          {draft.is_quantity_based ? (
            <div>
              <Label>Max quantity</Label>
              <Input
                type="number"
                value={draft.max_quantity ?? ''}
                onChange={(e) => patch({ max_quantity: Number(e.target.value) || null })}
              />
            </div>
          ) : null}
          {draft.is_tiered_quantity ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Extra unit monthly (USD)</Label>
                <Input value={extraMoPrice} onChange={(e) => { setExtraMoPrice(e.target.value); setDirty(true); }} />
              </div>
              <div>
                <Label>Extra unit yearly (USD / yr)</Label>
                <Input value={extraYrPrice} onChange={(e) => { setExtraYrPrice(e.target.value); setDirty(true); }} />
              </div>
            </div>
          ) : null}
          {draft.id === 'ai_assistant' ? (
            <div>
              <Label>Token overage ($/1M tokens, cents)</Label>
              <Input
                type="number"
                value={draft.ai_overage_per_million_cents ?? 120}
                onChange={(e) => patch({ ai_overage_per_million_cents: Number(e.target.value) || 0 })}
              />
            </div>
          ) : null}
          <Separator />
          <p className="text-sm font-medium">Available on plans</p>
          {ALL_PLAN_SLUGS.map((slug) => (
            <div key={slug} className="flex items-center justify-between text-sm">
              <span>{PLAN_SLUG_LABELS[slug]}</span>
              <Switch
                checked={(draft.available_on_plans || []).includes(slug)}
                onCheckedChange={() => togglePlanList('available_on_plans', slug)}
              />
            </div>
          ))}
          <p className="text-sm font-medium pt-2">Included free on plans</p>
          {ALL_PLAN_SLUGS.map((slug) => (
            <div key={`inc-${slug}`} className="flex items-center justify-between text-sm">
              <span>{PLAN_SLUG_LABELS[slug]}</span>
              <Switch
                checked={(draft.included_in_plans || []).includes(slug)}
                onCheckedChange={() => togglePlanList('included_in_plans', slug)}
              />
            </div>
          ))}
        </div>
        <PriceChangeConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          entityName={draft.name}
          detail={priceConfirm}
          confirming={saving}
          onConfirm={() => save(true)}
        />
        <SheetFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Discard
          </Button>
          <Button onClick={() => save(false)} disabled={saving || !dirty}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save add-on
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function EnterpriseConfigsPanel({ plans, addons, onRefresh }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({
    organization_id: '',
    spaces_limit: 10,
    domains_limit: 5,
    team_members_limit: 20,
    spaces_unlimited: false,
    domains_unlimited: false,
    team_members_unlimited: false,
    active_addon_ids: [],
    internal_notes: '',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, o] = await Promise.all([
        fetchEnterpriseConfigs(),
        axios.get(`${API_URL}/platform/organizations`, { params: { limit: 200 } }),
      ]);
      setConfigs(c);
      setOrgs(o.data?.items || []);
    } catch {
      toast.error('Failed to load enterprise configs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      organization_id: '',
      spaces_limit: 10,
      domains_limit: 5,
      team_members_limit: 20,
      spaces_unlimited: false,
      domains_unlimited: false,
      team_members_unlimited: false,
      active_addon_ids: [],
      internal_notes: '',
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      organization_id: row.organization_id,
      spaces_limit: row.spaces_limit ?? 10,
      domains_limit: row.domains_limit ?? 5,
      team_members_limit: row.team_members_limit ?? 20,
      spaces_unlimited: row.spaces_limit == null,
      domains_unlimited: row.domains_limit == null,
      team_members_unlimited: row.team_members_limit == null,
      active_addon_ids: row.active_addon_ids || [],
      internal_notes: row.internal_notes || '',
    });
    setModalOpen(true);
  };

  const toPayload = () => ({
    organization_id: form.organization_id,
    spaces_limit: form.spaces_unlimited ? null : Number(form.spaces_limit) || 0,
    domains_limit: form.domains_unlimited ? null : Number(form.domains_limit) || 0,
    team_members_limit: form.team_members_unlimited ? null : Number(form.team_members_limit) || 0,
    spaces_unlimited: form.spaces_unlimited,
    domains_unlimited: form.domains_unlimited,
    team_members_unlimited: form.team_members_unlimited,
    active_addon_ids: form.active_addon_ids,
    internal_notes: form.internal_notes,
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = toPayload();
      if (editing) {
        await patchEnterpriseConfig(editing.organization_id, payload);
        toast.success('Enterprise config updated');
      } else {
        await createEnterpriseConfig(payload);
        toast.success('Enterprise plan assigned');
      }
      setModalOpen(false);
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (orgId) => {
    if (!window.confirm('Remove enterprise config and revert workspace to trial?')) return;
    try {
      await deleteEnterpriseConfig(orgId);
      toast.success('Enterprise config removed');
      await load();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Enterprise plan</CardTitle>
            <CardDescription>
              Contact Sales only on the public page. Per-company limits and included add-ons are configured below.
            </CardDescription>
          </div>
          <Button onClick={openNew}>Assign enterprise plan</Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Spaces</TableHead>
                  <TableHead>Domains</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Add-ons</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((row) => (
                  <TableRow key={row.organization_id}>
                    <TableCell>{row.organization_name || row.organization_id}</TableCell>
                    <TableCell>{row.spaces_limit == null ? '∞' : row.spaces_limit}</TableCell>
                    <TableCell>{row.domains_limit == null ? '∞' : row.domains_limit}</TableCell>
                    <TableCell>{row.team_members_limit == null ? '∞' : row.team_members_limit}</TableCell>
                    <TableCell className="text-xs">{(row.active_addon_ids || []).join(', ') || '—'}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => remove(row.organization_id)}>
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={modalOpen} onOpenChange={setModalOpen}>
        <SheetContent className="overflow-y-auto sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{editing ? 'Edit enterprise config' : 'Assign enterprise plan'}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            {!editing ? (
              <div className="space-y-2">
                <Label>Company</Label>
                <Select value={form.organization_id} onValueChange={(v) => setForm((f) => ({ ...f, organization_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select workspace" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <LimitInput label="Spaces" value={form.spaces_unlimited ? 'unlimited' : form.spaces_limit} allowUnlimited onChange={(v) => setForm((f) => ({ ...f, spaces_limit: v === 'unlimited' ? 0 : v, spaces_unlimited: v === 'unlimited' }))} />
            <LimitInput label="Custom domains" value={form.domains_unlimited ? 'unlimited' : form.domains_limit} allowUnlimited onChange={(v) => setForm((f) => ({ ...f, domains_limit: v === 'unlimited' ? 0 : v, domains_unlimited: v === 'unlimited' }))} />
            <LimitInput label="Team members" value={form.team_members_unlimited ? 'unlimited' : form.team_members_limit} allowUnlimited onChange={(v) => setForm((f) => ({ ...f, team_members_limit: v === 'unlimited' ? 0 : v, team_members_unlimited: v === 'unlimited' }))} />
            <p className="text-sm font-medium">Included add-ons (no extra charge)</p>
            {(addons || []).map((a) => (
              <div key={a.id} className="flex justify-between text-sm">
                <span>{a.name}</span>
                <Switch
                  checked={form.active_addon_ids.includes(a.id)}
                  onCheckedChange={(on) =>
                    setForm((f) => ({
                      ...f,
                      active_addon_ids: on
                        ? [...f.active_addon_ids, a.id]
                        : f.active_addon_ids.filter((x) => x !== a.id),
                    }))
                  }
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label>Internal notes</Label>
              <Textarea rows={3} value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} />
            </div>
          </div>
          <SheetFooter>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function PreviewPanel({ preview, billCycle }) {
  const plans = preview?.plans || [];
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-3 gap-4">
        {plans.map((plan) => {
          const legacyPlan = {
            id: plan.internal_id || plan.id,
            price_usd: (plan.monthly_price_cents || 0) / 100,
            price_usd_yearly: (plan.yearly_price_cents || 0) / 100,
            pricing_display: plan.is_enterprise_contact ? 'contact_sales' : 'fixed',
            features: {},
          };
          const p = plan.is_enterprise_contact ? null : planDisplayPrice(legacyPlan, billCycle);
          return (
            <Card key={plan.id} className={plan.is_highlighted ? 'ring-2 ring-amber-400' : ''}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{plan.name}</CardTitle>
                  {plan.is_highlighted ? (
                    <Badge className="bg-amber-500">Most Popular</Badge>
                  ) : null}
                </div>
                <CardDescription className="text-xs">{plan.tagline}</CardDescription>
              </CardHeader>
              <CardContent>
                {plan.is_enterprise_contact ? (
                  <p className="font-medium">Contact Sales</p>
                ) : p ? (
                  <p className="text-xl font-bold">
                    ${p.amount.toLocaleString()}/mo
                    {p.note ? <span className="block text-xs font-normal text-slate-500">billed annually</span> : null}
                  </p>
                ) : null}
                <p className="text-xs text-slate-500 mt-2">
                  {plan.limits?.spaces === 'unlimited' ? '∞' : plan.limits?.spaces} spaces ·{' '}
                  {plan.limits?.team_members_included === -1 ? '∞' : plan.limits?.team_members_included} members
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {(preview?.addons || []).length > 0 ? (
        <div>
          <p className="text-sm font-medium mb-2">Active add-ons</p>
          <div className="flex flex-wrap gap-2">
            {preview.addons.map((a) => (
              <Badge key={a.id} variant="outline">
                {a.name} · {formatUsd(a.monthly_price_cents)}/mo
              </Badge>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function SuperAdminPricingPage() {
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [editPlan, setEditPlan] = useState(null);
  const [editAddon, setEditAddon] = useState(null);
  const [planSheetOpen, setPlanSheetOpen] = useState(false);
  const [addonSheetOpen, setAddonSheetOpen] = useState(false);
  const [previewCycle, setPreviewCycle] = useState('monthly');
  const [savingSettings, setSavingSettings] = useState(false);
  const [togglingPlan, setTogglingPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, s, prev] = await Promise.all([
        fetchAdminPlans(),
        fetchAdminAddons(),
        fetchSystemSettings(),
        fetchCatalogPreview(),
      ]);
      setPlans(p);
      setAddons(a);
      setSettings(s);
      setPreview(prev);
    } catch {
      toast.error('Failed to load pricing catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const togglePlanField = async (plan, field, value) => {
    setTogglingPlan(plan.id);
    try {
      await patchAdminPlan(plan.id, { [field]: value });
      await load();
      toast.success('Plan updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setTogglingPlan(null);
    }
  };

  const toggleAddonActive = async (addon, active) => {
    try {
      await patchAdminAddon(addon.id, { is_active: active });
      await load();
      toast.success(active ? 'Add-on enabled' : 'Add-on disabled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const updated = await patchSystemSettings(settings);
      setSettings(updated);
      toast.success('System settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const paidPlans = useMemo(
    () => plans.filter((p) => ALL_PLAN_SLUGS.includes(p.id)).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [plans]
  );

  if (loading && !plans.length) {
    return (
      <SuperAdminShell title="Plans management" testId="super-admin-plans">
        <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading catalog…
        </div>
      </SuperAdminShell>
    );
  }

  return (
    <SuperAdminShell
      title="Plans & pricing"
      description="Manage public plans, add-on catalog, enforcement rules, and preview the customer pricing page."
      testId="super-admin-pricing"
    >
      <Tabs defaultValue="plans" className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto gap-1">
          <TabsTrigger value="plans">Plans</TabsTrigger>
          <TabsTrigger value="addons">Add-ons</TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="w-3.5 h-3.5 mr-1 inline" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="system">System enforcement</TabsTrigger>
        </TabsList>

        <TabsContent value="plans" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-[Manrope]">Plans overview</CardTitle>
              <CardDescription>Growing, Scaling, and Enterprise tiers shown on the public pricing page.</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Monthly</TableHead>
                    <TableHead>Yearly</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Popular</TableHead>
                    <TableHead>Contact sales</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paidPlans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div className="font-medium">{plan.name}</div>
                        <div className="text-xs text-slate-500">{plan.tagline}</div>
                      </TableCell>
                      <TableCell>
                        {plan.is_enterprise_contact ? '—' : formatUsd(plan.monthly_price_cents)}
                      </TableCell>
                      <TableCell>
                        {plan.is_enterprise_contact ? '—' : formatUsd(plan.yearly_price_cents)}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!plan.is_active}
                          disabled={togglingPlan === plan.id}
                          onCheckedChange={(v) => togglePlanField(plan, 'is_active', v)}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!plan.is_highlighted}
                          disabled={togglingPlan === plan.id}
                          onCheckedChange={(v) => togglePlanField(plan, 'is_highlighted', v)}
                        />
                      </TableCell>
                      <TableCell>
                        {plan.id === 'enterprise' ? (
                          <Switch
                            checked={!!plan.is_enterprise_contact}
                            disabled={togglingPlan === plan.id}
                            onCheckedChange={(v) => togglePlanField(plan, 'is_enterprise_contact', v)}
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditPlan(plan);
                            setPlanSheetOpen(true);
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="enterprise" className="space-y-4">
          <EnterpriseConfigsPanel plans={plans} addons={addons} onRefresh={load} />
        </TabsContent>

        <TabsContent value="addons" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-[Manrope]">Add-on pricing</CardTitle>
              <CardDescription>
                Global add-on prices. Changes affect new subscriptions immediately; active subscribers are notified
                and charged the new price at their next renewal.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Monthly</TableHead>
                    <TableHead>Yearly</TableHead>
                    <TableHead>Plans</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {addons.map((addon) => (
                    <TableRow key={addon.id}>
                      <TableCell>
                        <div className="font-medium">{addon.name}</div>
                        <div className="text-xs text-slate-500 line-clamp-1">{addon.description}</div>
                      </TableCell>
                      <TableCell className="text-xs">{addon.unit || '—'}</TableCell>
                      <TableCell>{formatUsd(addon.monthly_price_cents)}</TableCell>
                      <TableCell>{formatUsd(addon.yearly_price_cents)}</TableCell>
                      <TableCell className="text-xs">
                        {(addon.available_on_plans || []).map((s) => PLAN_SLUG_LABELS[s] || s).join(', ') || '—'}
                      </TableCell>
                      <TableCell>
                        <Switch checked={!!addon.is_active} onCheckedChange={(v) => toggleAddonActive(addon, v)} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditAddon(addon);
                            setAddonSheetOpen(true);
                          }}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4">
              <div>
                <CardTitle className="text-base font-[Manrope] flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  Pricing page preview
                </CardTitle>
                <CardDescription>Live view of active plans and add-ons as customers see them.</CardDescription>
              </div>
              <div className="flex rounded-full border p-1">
                <button
                  type="button"
                  className={`px-3 py-1 text-xs rounded-full ${previewCycle === 'monthly' ? 'bg-slate-900 text-white' : ''}`}
                  onClick={() => setPreviewCycle('monthly')}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={`px-3 py-1 text-xs rounded-full ${previewCycle === 'yearly' ? 'bg-slate-900 text-white' : ''}`}
                  onClick={() => setPreviewCycle('yearly')}
                >
                  Yearly
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <PreviewPanel preview={preview} billCycle={previewCycle} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-[Manrope]">System enforcement</CardTitle>
              <CardDescription>Control how plan limits are applied across the platform.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6 max-w-lg">
              {settings ? (
                <>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label>Enforce plan limits</Label>
                      <p className="text-xs text-slate-500">Block actions when limits are exceeded (vs warn only).</p>
                    </div>
                    <Switch
                      checked={!!settings.enforce_plan_limits}
                      onCheckedChange={(v) => setSettings((s) => ({ ...s, enforce_plan_limits: v }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Grace period on downgrade (days)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={settings.grace_period_on_downgrade_days ?? 7}
                      onChange={(e) =>
                        setSettings((s) => ({
                          ...s,
                          grace_period_on_downgrade_days: Number(e.target.value) || 0,
                        }))
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>AI token overage behavior</Label>
                    <Select
                      value={settings.ai_token_overage_behavior || 'warn'}
                      onValueChange={(v) => setSettings((s) => ({ ...s, ai_token_overage_behavior: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="block">Block</SelectItem>
                        <SelectItem value="warn">Warn</SelectItem>
                        <SelectItem value="auto_charge">Auto-charge</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label>Allow purchase without payment method</Label>
                      <p className="text-xs text-slate-500">Useful for enterprise invoicing.</p>
                    </div>
                    <Switch
                      checked={!!settings.allow_purchase_without_payment_method}
                      onCheckedChange={(v) =>
                        setSettings((s) => ({ ...s, allow_purchase_without_payment_method: v }))
                      }
                    />
                  </div>
                  <Button onClick={saveSettings} disabled={savingSettings}>
                    {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save settings
                  </Button>
                </>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PlanEditorSheet
        plan={editPlan}
        open={planSheetOpen}
        onOpenChange={setPlanSheetOpen}
        addons={addons}
        onSaved={load}
      />
      <AddonEditorSheet
        addon={editAddon}
        open={addonSheetOpen}
        onOpenChange={setAddonSheetOpen}
        onSaved={load}
      />
    </SuperAdminShell>
  );
}
